import React, { useState, useMemo, useCallback, useRef, useImperativeHandle, forwardRef } from 'react'
import {
  computeFlip,
  DEFAULT_FLIP_INPUTS,
  DEFAULT_PURCHASE_ITEMS,
  DEFAULT_HOLDING_ITEMS,
  DEFAULT_SELLING_ITEMS,
  DEFAULT_REHAB,
} from './flipCalcEngine'
import { calcMonthlyPayment } from './roiCalcEngine'
import { openPrintView, fmt as pf, cardsHTML, pillsHTML, plRowsHTML, tableHTML, inputsGridHTML, sectionHTML, costBreakdownChartSVG } from './printCalculator'

// ── Formatters ────────────────────────────────────────────────────────────────
const currency = (v, d = 0) => {
  if (v === null || v === undefined || !isFinite(v)) return '—'
  const s = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })
  return v < 0 ? `-$${s}` : `$${s}`
}
const pct = (v, d = 2) => isFinite(v) ? `${(v * 100).toFixed(d)}%` : '—'
const mult = (v, d = 2) => isFinite(v) ? `${v.toFixed(d)}×` : '—'

// ── Form Primitives ──────────────────────────────────────────────────────────
function Field({ label, hint, children }) {
  return (
    <div>
      <label className="calc-field__label">{label}</label>
      {children}
      {hint && <p className="calc-field__hint">{hint}</p>}
    </div>
  )
}

function Inp({ value, onChange, pre, suf, step = 'any', min = 0 }) {
  const [display, setDisplay] = useState(String(value))
  const [focused, setFocused] = useState(false)
  React.useEffect(() => { if (!focused) setDisplay(String(value)) }, [value, focused])
  const inputCls = `calc-input${pre ? ' calc-input--has-pre' : ''}${suf ? ' calc-input--has-suf' : ''}`
  return (
    <div className="calc-input-wrap">
      {pre && <span className="calc-input-wrap__affix calc-input-wrap__affix--pre">{pre}</span>}
      <input
        type="number"
        step={step}
        min={min}
        value={focused ? display : value}
        onFocus={() => { setFocused(true); setDisplay(String(value)) }}
        onChange={(e) => { setDisplay(e.target.value); const n = parseFloat(e.target.value); if (!isNaN(n)) onChange(n) }}
        onBlur={() => { setFocused(false); if (display === '' || isNaN(parseFloat(display))) onChange(0) }}
        className={inputCls}
      />
      {suf && <span className="calc-input-wrap__affix calc-input-wrap__affix--suf">{suf}</span>}
    </div>
  )
}

function Group({ title, children }) {
  return (
    <div className="calc-group">
      <div className="calc-group__header">
        <div className="calc-group__title">{title}</div>
      </div>
      <div className="calc-group__body">{children}</div>
    </div>
  )
}

// ── Editable Number Input (allows clearing to empty, reverts to 0 on blur) ──
function NumInput({ value, onChange, readOnly, className, style, min = 0, step = '100' }) {
  const [display, setDisplay] = useState(String(value))
  const [focused, setFocused] = useState(false)
  React.useEffect(() => { if (!focused) setDisplay(String(value)) }, [value, focused])
  return (
    <input
      type="number"
      min={min}
      step={step}
      readOnly={readOnly}
      className={className}
      style={style}
      value={focused ? display : value}
      onFocus={() => { setFocused(true); setDisplay(String(value)) }}
      onChange={(e) => { setDisplay(e.target.value); const n = parseFloat(e.target.value); if (!isNaN(n) && !readOnly) onChange(n) }}
      onBlur={() => { setFocused(false); if (display === '' || isNaN(parseFloat(display))) onChange(0) }}
    />
  )
}

// ── Dynamic Cost List ────────────────────────────────────────────────────────
function DynamicCostList({ items, onUpdate, onAdd, onRemove, readonlyId, readonlyValue }) {
  return (
    <div className="calc-cost-list">
      {items.map((item) => {
        const isReadonly = item.id === readonlyId
        return (
          <div key={item.id} className={`calc-cost-list__row${isReadonly ? ' calc-cost-list__row--readonly' : ''}`}>
            <input
              type="text"
              value={item.label}
              onChange={(e) => !isReadonly && onUpdate(item.id, 'label', e.target.value)}
              readOnly={isReadonly}
              placeholder="Item name"
              className={`calc-cost-list__name${isReadonly ? ' calc-cost-list__name--readonly' : ''}`}
            />
            <div className="calc-cost-list__amount-wrap">
              <span className="calc-cost-list__amount-pre">$</span>
              <NumInput
                value={isReadonly ? (readonlyValue ?? item.amount) : item.amount}
                onChange={(n) => onUpdate(item.id, 'amount', n)}
                readOnly={isReadonly}
                className={`calc-cost-list__amount${isReadonly ? ' calc-cost-list__amount--readonly' : ''}`}
              />
            </div>
            {isReadonly ? (
              <span style={{ flexShrink: 0, width: 14 }} />
            ) : (
              <button onClick={() => onRemove(item.id)} className="calc-cost-list__remove" title="Remove">
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )
      })}
      <button onClick={onAdd} className="calc-cost-list__add">
        <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        Add item
      </button>
    </div>
  )
}

// ── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, teal, green, rose }) {
  let variant = ''
  if (teal) variant = ' calc-stat--teal'
  else if (green) variant = ' calc-stat--green'
  else if (rose) variant = ' calc-stat--rose'

  return (
    <div className={`calc-stat${variant}`}>
      <div className="calc-stat__label">{label}</div>
      <div className="calc-stat__value">{value}</div>
      {sub && <div className="calc-stat__sub">{sub}</div>}
    </div>
  )
}

// ── Section Banner ───────────────────────────────────────────────────────────
function SectionBanner({ title }) {
  return (
    <div className="calc-banner">
      <div className="calc-banner__title">{title}</div>
    </div>
  )
}

// ── Cost Breakdown Chart ─────────────────────────────────────────────────────
function CostBreakdownChart({ r }) {
  const W = 640, H = 150
  const PAD = { t: 40, r: 20, b: 44, l: 20 }
  const iW = W - PAD.l - PAD.r
  const barH = 32

  const segs = [
    { label: 'Purchase', value: r.purchaseCostTotal, color: '#128292' },
    { label: 'Holding',  value: r.holdingCostTotal,  color: '#3D9AA7' },
    { label: 'Rehab',    value: r.rehabCostTotal,    color: '#F16722' },
    { label: 'Selling',  value: r.sellingCostTotal,  color: '#CA1551' },
  ]
  const maxVal = Math.max(r.totalCosts, r.salePrice, 1) * 1.05

  let xCursor = PAD.l
  const bars = segs.map((seg) => {
    const w = (seg.value / maxVal) * iW
    const b = { ...seg, x: xCursor, w }
    xCursor += w
    return b
  })

  const spX = PAD.l + (r.salePrice / maxVal) * iW
  const tcX = PAD.l + (r.totalCosts / maxVal) * iW
  const profit = r.totalProfit >= 0
  const lineColor = profit ? '#5AC471' : '#CA1551'

  return (
    <div>
      <div className="calc-legend">
        {segs.map(({ label, color }) => (
          <div key={label} className="calc-legend__item">
            <span className="calc-legend__swatch" style={{ backgroundColor: color }} />
            {label}
          </div>
        ))}
        <div className="calc-legend__item calc-legend__item--right">
          <svg width="20" height="3"><line x1="0" y1="1.5" x2="20" y2="1.5" stroke={lineColor} strokeWidth="2" strokeDasharray="4 2" /></svg>
          Sale Price
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 150 }}>
        <defs>
          <clipPath id="flip-bar-clip">
            <rect x={PAD.l} y={PAD.t} width={iW} height={barH} rx="4" />
          </clipPath>
        </defs>
        <rect x={PAD.l} y={PAD.t} width={iW} height={barH} rx="4" fill="#EFF2F6" />
        <g clipPath="url(#flip-bar-clip)">
          {bars.map((b) => (
            <rect key={b.label} x={b.x} y={PAD.t} width={Math.max(b.w, 1)} height={barH} fill={b.color} />
          ))}
        </g>
        {bars.map((b) =>
          b.w > 50 ? (
            <text key={b.label + 'l'} x={b.x + b.w / 2} y={PAD.t + barH / 2 + 4} textAnchor="middle" fontSize="10" fontWeight="bold" fill="white">{b.label}</text>
          ) : null
        )}
        <line x1={tcX} y1={PAD.t - 2} x2={tcX} y2={PAD.t + barH + 2} stroke="#66707D" strokeWidth="1" />
        <text x={tcX} y={PAD.t - 6} textAnchor="middle" fontSize="11" fontWeight="600" fill="#66707D">Total Costs</text>
        <line x1={spX} y1={PAD.t - 14} x2={spX} y2={PAD.t + barH + 16} stroke={lineColor} strokeWidth="2" strokeDasharray="4 2" />
        <text x={spX} y={PAD.t - 18} textAnchor="middle" fontSize="11" fontWeight="700" fill={lineColor}>Sale Price</text>
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <text key={f} x={PAD.l + f * iW} y={PAD.t + barH + 16} textAnchor="middle" fontSize="11" fontWeight="600" fill="#66707D">{currency(maxVal * f, 0)}</text>
        ))}
      </svg>
    </div>
  )
}

// ── Constants ────────────────────────────────────────────────────────────────
const TABS = ['Summary', 'Rehab Log']
const COST_TYPES = ['Labor', 'Material', 'Overhead', 'Sub-contractor', 'Other']
const DEFAULT_MORTGAGE = { downPct: 20, interestRate: 7, mortgageYears: 30 }

// ── ID Counter ───────────────────────────────────────────────────────────────
function useIdCounter(start) {
  const ref = useRef(start)
  return () => ref.current++
}

// ── Main Component ──────────────────────────────────────────────────────────
const FixFlipAnalyser = forwardRef(function FixFlipAnalyser({ initialPurchasePrice, initialSalePrice, initialPropertyTax, address = '' }, ref) {
  const [inp, setInp] = useState(() => ({
    ...DEFAULT_FLIP_INPUTS,
    ...(initialPurchasePrice != null && { purchasePrice: Math.round(initialPurchasePrice) }),
    ...(initialSalePrice     != null && { salePrice:     Math.round(initialSalePrice) }),
  }))
  const [purchaseItems, setPurchaseItems] = useState(DEFAULT_PURCHASE_ITEMS)
  const [holdingItems, setHoldingItems]   = useState(() => {
    if (initialPropertyTax != null) {
      return DEFAULT_HOLDING_ITEMS.map((item) =>
        item.id === 2 ? { ...item, amount: Math.round(initialPropertyTax / 12) } : item
      )
    }
    return DEFAULT_HOLDING_ITEMS
  })
  const [sellingItems, setSellingItems]   = useState(DEFAULT_SELLING_ITEMS)
  const [rehab, setRehab]                 = useState(DEFAULT_REHAB)
  const [tab, setTab]                     = useState('Summary')
  const [targetROI, setTargetROI]         = useState(null) // null = MAO mode inactive
  const [mortgageMode, setMortgageMode]   = useState(true)
  const [mortgage, setMortgage]           = useState(DEFAULT_MORTGAGE)

  const nextPId = useIdCounter(DEFAULT_PURCHASE_ITEMS.length + 1)
  const nextHId = useIdCounter(DEFAULT_HOLDING_ITEMS.length + 1)
  const nextSId = useIdCounter(DEFAULT_SELLING_ITEMS.length + 1)
  const nextRId = useIdCounter(DEFAULT_REHAB.length + 1)

  const set    = (k) => (v) => setInp((prev) => ({ ...prev, [k]: v }))
  const setMtg = (k) => (v) => setMortgage((prev) => ({ ...prev, [k]: v }))

  const makeAdd    = (setter, nextId, defaults = {}) => () =>
    setter((prev) => [...prev, { id: nextId(), label: '', amount: 0, ...defaults }])
  const makeRemove = (setter) => (id) =>
    setter((prev) => prev.filter((i) => i.id !== id))
  const makeUpdate = (setter) => (id, key, val) =>
    setter((prev) => prev.map((i) => i.id === id ? { ...i, [key]: val } : i))

  const computedMortgagePayment = useMemo(() => {
    if (!mortgageMode) return 0
    const loanAmount = inp.purchasePrice * (1 - mortgage.downPct / 100)
    return calcMonthlyPayment(loanAmount, mortgage.interestRate / 100, mortgage.mortgageYears)
  }, [mortgageMode, inp.purchasePrice, mortgage])

  const effectiveHoldingItems = useMemo(() => {
    if (!mortgageMode) return holdingItems
    return holdingItems.map((item) =>
      item.id === 1 ? { ...item, amount: computedMortgagePayment } : item
    )
  }, [mortgageMode, holdingItems, computedMortgagePayment])

  const r = useMemo(
    () => computeFlip(inp, rehab, purchaseItems, effectiveHoldingItems, sellingItems),
    [inp, rehab, purchaseItems, effectiveHoldingItems, sellingItems]
  )
  const isProfit = r.totalProfit >= 0

  // MAO reverse-solve: given target ROI, what should purchase price be?
  const mao = useMemo(() => {
    if (targetROI === null) return null
    const roiDecimal = targetROI / 100
    const otherCosts = r.purchaseItemsTotal + r.holdingCostTotal + r.rehabCostTotal
    const sellingTotal = r.sellingCostTotal
    if (roiDecimal <= -1) return null // impossible
    const cashInvested = (inp.salePrice - sellingTotal) / (1 + roiDecimal)
    const pp = Math.max(0, Math.round(cashInvested - otherCosts))
    return pp
  }, [targetROI, inp.salePrice, r.purchaseItemsTotal, r.holdingCostTotal, r.rehabCostTotal, r.sellingCostTotal])

  useImperativeHandle(ref, () => ({
    getInputs:  () => ({ ...inp }),
    getResults: () => ({ ...r }),
    setInputs:  (updates) => setInp((prev) => ({ ...prev, ...updates })),
  }), [inp, r])

  const handlePrint = useCallback(() => {
    const c = pf.currency, p = pf.pct
    const kpis = [
      { label: 'Total Profit',  value: c(r.totalProfit),  color: isProfit ? '#5AC471' : '#CA1551' },
      { label: 'Profit Margin', value: p(r.profitMargin),  color: isProfit ? '#5AC471' : '#CA1551' },
      { label: 'ROI',           value: p(r.roi),           color: isProfit ? '#5AC471' : '#CA1551' },
      { label: 'Total Costs',   value: c(r.totalCosts),   color: '#3C4654' },
      { label: 'Sale Price',    value: c(r.salePrice),    color: '#3C4654' },
    ]

    const inputsHTML = inputsGridHTML([
      { title: 'Deal', items: [
        { label: 'Purchase Price', value: c(inp.purchasePrice) },
        { label: 'Sale Price (ARV)', value: c(inp.salePrice) },
        { label: 'Holding Period', value: `${inp.holdingMonths} months` },
      ]},
      { title: 'Purchase Costs', items: [
        { label: 'Purchase Price', value: c(inp.purchasePrice) },
        ...purchaseItems.map(item => ({ label: item.label || '—', value: c(item.amount) })),
        { label: 'Total', value: c(r.purchaseCostTotal) },
      ]},
      { title: 'Holding Costs (per month)', items: [
        ...effectiveHoldingItems.map(item => ({ label: item.label || '—', value: `${c(item.amount)}/mo` })),
        { label: 'Monthly Total', value: `${c(r.monthlyHoldingCost)}/mo` },
        { label: `Total (${inp.holdingMonths} mo)`, value: c(r.holdingCostTotal) },
      ]},
      { title: 'Selling Costs', items: [
        ...sellingItems.map(item => ({ label: item.label || '—', value: c(item.amount) })),
        { label: 'Total', value: c(r.sellingCostTotal) },
      ]},
      ...(rehab.length > 0 ? [{ title: 'Rehab Costs', items: [
        { label: 'Line Items', value: `${rehab.length}` },
        { label: 'Total', value: c(r.rehabCostTotal) },
      ]}] : []),
    ])

    const pages = [
      { title: 'Summary', html:
        sectionHTML('Deal Metrics', cardsHTML([
          { label: 'Total Profit',    value: c(r.totalProfit),    sub: isProfit ? 'Profitable' : 'Loss', color: isProfit ? '#5AC471' : '#CA1551' },
          { label: 'Profit Margin',   value: p(r.profitMargin),   sub: 'Profit ÷ total costs', color: isProfit ? '#128292' : '#CA1551' },
          { label: 'ROI',             value: p(r.roi),             sub: 'Profit ÷ cash invested', color: isProfit ? '#128292' : '#CA1551' },
          { label: 'Equity Multiple', value: mult(r.equityMultiple), sub: 'Sale ÷ cash invested' },
          { label: 'Total Costs',     value: c(r.totalCosts),     sub: 'All-in costs' },
          { label: 'Cash Invested',   value: c(r.cashInvested),   sub: 'Excl. selling costs' },
          { label: 'Sale Price',      value: c(r.salePrice),      sub: 'Target sale' },
          { label: 'Rehab Costs',     value: c(r.rehabCostTotal), sub: `${rehab.length} line item${rehab.length !== 1 ? 's' : ''}` },
        ])) +
        sectionHTML('Cost Breakdown', costBreakdownChartSVG(r, { currency: c })) +
        sectionHTML('Cost Stack',
          '<div class="two-col">' +
          '<div>' +
          plRowsHTML([
            { label: 'Purchase Costs', value: c(r.purchaseCostTotal), bold: true },
            { label: 'Purchase Price', value: c(inp.purchasePrice), indent: true },
            ...purchaseItems.map(item => ({ label: item.label || '—', value: c(item.amount), indent: true })),
            { divider: true },
            { label: `Holding Costs (${inp.holdingMonths} mo)`, value: c(r.holdingCostTotal), bold: true },
            ...effectiveHoldingItems.map(item => ({ label: item.label || '—', value: `${c(item.amount)}/mo`, indent: true })),
            { divider: true },
            { label: 'Rehab Costs', value: c(r.rehabCostTotal), bold: true },
            { divider: true },
            { label: 'Selling Costs', value: c(r.sellingCostTotal), bold: true },
            ...sellingItems.map(item => ({ label: item.label || '—', value: c(item.amount), indent: true })),
            { divider: true, heavy: true },
            { label: 'Total Costs', value: c(r.totalCosts), bold: true },
          ]) +
          '</div><div>' +
          plRowsHTML([
            { label: 'Deal Summary', value: '', bold: true },
            { label: 'Sale Price', value: c(r.salePrice) },
            { label: '− Total Costs', value: `(${c(r.totalCosts)})`, red: true },
            { divider: true },
            { label: '= Total Profit', value: c(r.totalProfit), bold: true },
          ]) +
          '<div style="margin-top:16px">' +
          cardsHTML([
            { label: 'Purchase', value: c(r.purchaseCostTotal), color: '#128292' },
            { label: 'Holding', value: c(r.holdingCostTotal), color: '#3D9AA7' },
            { label: 'Rehab', value: c(r.rehabCostTotal), color: '#F16722' },
            { label: 'Selling', value: c(r.sellingCostTotal), color: '#CA1551' },
          ]) +
          '</div></div></div>'
        )
      },
      ...(rehab.length > 0 ? [{ title: 'Rehab Log', html: sectionHTML('Rehab Transactions', (() => {
        const rehabRows = rehab.map(row => ({
          date: row.date || '—',
          costType: row.costType,
          description: row.description || '—',
          amount: c(row.amount),
        }))
        rehabRows.push({ date: '', costType: '', description: '<strong>Total</strong>', amount: `<strong>${c(r.rehabCostTotal)}</strong>`, _highlight: true })
        return tableHTML(
          [
            { label: 'Date', key: 'date' },
            { label: 'Cost Type', key: 'costType' },
            { label: 'Description', key: 'description' },
            { label: 'Amount', key: 'amount', align: 'right' },
          ],
          rehabRows
        )
      })()) }] : []),
    ]
    openPrintView({ title: 'Fix & Flip Analyzer', subtitle: 'Analyze deal profitability across purchase, holding, rehab & sale', address, kpis, inputsHTML, pages })
  }, [r, inp, isProfit, rehab, purchaseItems, effectiveHoldingItems, sellingItems])

  return (
    <div className="calc">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="calc-header">
        <div>
          <div className="calc-header__title">Fix &amp; Flip Analyzer</div>
          <div className="calc-header__subtitle">Analyze deal profitability across purchase, holding, rehab &amp; sale</div>
        </div>
        <div className="calc-header__kpis">
          {[
            { label: 'Total Profit',  val: currency(r.totalProfit),  neg: !isProfit },
            { label: 'Profit Margin', val: pct(r.profitMargin),      neg: !isProfit },
            { label: 'ROI',           val: pct(r.roi),               neg: !isProfit },
            { label: 'Total Costs',   val: currency(r.totalCosts),   neutral: true },
            { label: 'Sale Price',    val: currency(r.salePrice),    neutral: true },
          ].map(({ label, val, neg, neutral }, i, arr) => (
            <React.Fragment key={label}>
              <div className="calc-header__kpi">
                <div className="calc-header__kpi-label">{label}</div>
                <div className="calc-header__kpi-value" style={{ color: neutral ? 'var(--clr-charcoal)' : neg ? 'var(--clr-rose)' : '#5AC471' }}>{val}</div>
              </div>
              {i < arr.length - 1 && <div className="calc-header__kpi-sep" />}
            </React.Fragment>
          ))}
        </div>
        <button className="calc-header__print" onClick={handlePrint} title="Print Report">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" /><rect x="6" y="14" width="12" height="8" />
          </svg>
        </button>
      </div>

      {/* ── Tab Bar ────────────────────────────────────────────────────── */}
      <div className="calc-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`calc-tabs__btn${tab === t ? ' calc-tabs__btn--active' : ''}`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <div className="calc-body">

        {/* ── LEFT: Inputs ──────────────────────────────────────────── */}
        <div className="calc-sidebar">

          <Group title="Purchase Costs">
            <Field label="Purchase Price">
              <Inp value={inp.purchasePrice} onChange={set('purchasePrice')} pre="$" step="1000" />
            </Field>
            <div className="calc-section-divider">
              <p className="calc-section-label">Additional Costs</p>
              <DynamicCostList
                items={purchaseItems}
                onUpdate={makeUpdate(setPurchaseItems)}
                onRemove={makeRemove(setPurchaseItems)}
                onAdd={makeAdd(setPurchaseItems, nextPId)}
              />
            </div>
            <div className="calc-cost-summary calc-cost-summary--teal">
              <span className="calc-cost-summary__label">Total Purchase Costs</span>
              <span className="calc-cost-summary__value">{currency(r.purchaseCostTotal)}</span>
            </div>
          </Group>

          <Group title="Holding Costs">
            <Field label="# of Months">
              <Inp value={inp.holdingMonths} onChange={set('holdingMonths')} step="1" min={0} />
            </Field>
            <div className="calc-section-divider">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <p className="calc-section-label" style={{ marginBottom: 0 }}>Monthly Costs</p>
                <button onClick={() => setMortgageMode((v) => !v)} className="calc-toggle-link">
                  {mortgageMode ? 'Hide Mortgage Calculator' : 'Mortgage Calculator'}
                </button>
              </div>

              {mortgageMode && (
                <div className="calc-mortgage-box">
                  <div className="calc-mortgage-box__grid">
                    <Field label="Down %">
                      <Inp value={mortgage.downPct} onChange={setMtg('downPct')} suf="%" step="1" />
                    </Field>
                    <Field label="Rate %">
                      <Inp value={mortgage.interestRate} onChange={setMtg('interestRate')} suf="%" step="0.125" />
                    </Field>
                    <Field label="Years">
                      <Inp value={mortgage.mortgageYears} onChange={setMtg('mortgageYears')} step="1" />
                    </Field>
                  </div>
                  <div className="calc-mortgage-box__summary">
                    <div>
                      <p className="calc-mortgage-box__summary-label">Loan</p>
                      <p className="calc-mortgage-box__summary-value">{currency(inp.purchasePrice * (1 - mortgage.downPct / 100), 0)}</p>
                    </div>
                    <div>
                      <p className="calc-mortgage-box__summary-label">Payment</p>
                      <p className="calc-mortgage-box__summary-value">{currency(computedMortgagePayment)}/mo</p>
                    </div>
                    <div>
                      <p className="calc-mortgage-box__summary-label">Down</p>
                      <p className="calc-mortgage-box__summary-value">{currency(inp.purchasePrice * mortgage.downPct / 100, 0)}</p>
                    </div>
                  </div>
                </div>
              )}
              <DynamicCostList
                items={holdingItems}
                onUpdate={makeUpdate(setHoldingItems)}
                onRemove={makeRemove(setHoldingItems)}
                onAdd={makeAdd(setHoldingItems, nextHId)}
                readonlyId={mortgageMode ? 1 : undefined}
                readonlyValue={mortgageMode ? computedMortgagePayment : undefined}
              />
            </div>
            <div className="calc-cost-summary calc-cost-summary--teal">
              <span className="calc-cost-summary__label">Total Holding Costs</span>
              <span className="calc-cost-summary__value">{currency(r.holdingCostTotal)}</span>
            </div>
          </Group>

          <Group title="Sale &amp; Selling Costs">
            <Field label="Sale Price">
              <Inp value={inp.salePrice} onChange={set('salePrice')} pre="$" step="1000" />
            </Field>
            <div className="calc-section-divider">
              <p className="calc-section-label">Selling Costs</p>
              <DynamicCostList
                items={sellingItems}
                onUpdate={makeUpdate(setSellingItems)}
                onRemove={makeRemove(setSellingItems)}
                onAdd={makeAdd(setSellingItems, nextSId)}
              />
            </div>
            <div className="calc-cost-summary calc-cost-summary--orange">
              <span className="calc-cost-summary__label">Total Selling Costs</span>
              <span className="calc-cost-summary__value">{currency(r.sellingCostTotal)}</span>
            </div>
          </Group>
        </div>

        {/* ── RIGHT: Tabbed Dashboard ──────────────────────────────────── */}
        <div className="calc-dashboard">

          {/* SUMMARY TAB */}
          {tab === 'Summary' && (
            <>
              <SectionBanner title="Deal Metrics" />
              <div className="calc-grid-4">
                <StatCard label="Total Profit"    value={currency(r.totalProfit)}    sub={isProfit ? 'Deal is profitable' : 'Deal is a loss'} green={isProfit} rose={!isProfit} />
                <StatCard label="Profit Margin"   value={pct(r.profitMargin)}        sub="Profit ÷ total costs" teal={isProfit} rose={!isProfit} />
                <StatCard label="ROI"             value={pct(r.roi)}                 sub="Profit ÷ cash invested" teal={isProfit} rose={!isProfit} />
                <StatCard label="Equity Multiple" value={mult(r.equityMultiple)}     sub="Sale ÷ cash invested" />
              </div>
              <div className="calc-grid-4">
                <StatCard label="Total Costs"   value={currency(r.totalCosts)}     sub="All-in costs" />
                <StatCard label="Cash Invested" value={currency(r.cashInvested)}   sub="Excl. selling costs" />
                <StatCard label="Sale Price"    value={currency(r.salePrice)}      sub="Target sale" />
                <StatCard label="Rehab Costs"   value={currency(r.rehabCostTotal)} sub={`${rehab.length} line item${rehab.length !== 1 ? 's' : ''}`} />
              </div>

              <SectionBanner title="MAO Calculator" />
              <div className="calc-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span className="calc-field__label" style={{ margin: 0 }}>Target ROI %</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input
                          type="number"
                          step="1"
                          value={targetROI === null ? Math.round(r.roi * 100) : targetROI}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value)
                            setTargetROI(isNaN(v) ? 0 : Math.max(0, Math.min(100, v)))
                          }}
                          style={{ width: 60, textAlign: 'right', padding: '2px 6px', fontSize: 14, fontWeight: 600, borderRadius: 6, border: '1px solid var(--clr-border)', color: 'var(--clr-charcoal)' }}
                        />
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--clr-grey)' }}>%</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={targetROI === null ? Math.round(r.roi * 100) : targetROI}
                      onChange={(e) => setTargetROI(parseInt(e.target.value, 10))}
                      className="calc-slider"
                      style={{ width: '100%' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--clr-grey)', marginTop: 2 }}>
                      <span>0%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, minWidth: 160, minHeight: 100 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', color: 'var(--clr-grey)', letterSpacing: 0.35 }}>Max Allowable Offer</span>
                    <span style={{ fontSize: 28, fontWeight: 700, color: mao !== null && mao !== inp.purchasePrice ? 'var(--clr-teal)' : 'var(--clr-charcoal)' }}>{currency(mao !== null ? mao : inp.purchasePrice)}</span>
                    <button
                      onClick={() => { set('purchasePrice')(mao); setTargetROI(null) }}
                      style={{ padding: '5px 14px', fontSize: 13, fontWeight: 600, color: 'var(--clr-white)', background: 'var(--clr-teal)', borderRadius: 50, border: 'none', cursor: 'pointer', visibility: mao !== null && mao !== inp.purchasePrice ? 'visible' : 'hidden' }}
                    >
                      Apply as Purchase Price
                    </button>
                    <button
                      onClick={() => setTargetROI(null)}
                      style={{ padding: 0, fontSize: 12, fontWeight: 500, color: 'var(--clr-grey)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', visibility: targetROI !== null ? 'visible' : 'hidden' }}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>

              <SectionBanner title="Cost Breakdown" />
              <div className="calc-card">
                <CostBreakdownChart r={r} />
              </div>

              <SectionBanner title="Cost Stack" />
              <div className="calc-card">
                <div className="calc-statement-grid">

                  {/* Left: itemised costs */}
                  <div>
                    <p className="calc-cost-stack__heading">All Costs</p>

                    <div className="calc-cost-stack__row">
                      <span className="calc-cost-stack__row-label calc-cost-stack__row-label--bold">Purchase Costs</span>
                      <span className="calc-cost-stack__row-value calc-cost-stack__row-value--bold">{currency(r.purchaseCostTotal)}</span>
                    </div>
                    <div className="calc-cost-stack__row calc-cost-stack__row--indent">
                      <span className="calc-cost-stack__row-label">Purchase Price</span>
                      <span className="calc-cost-stack__row-value">{currency(inp.purchasePrice)}</span>
                    </div>
                    {purchaseItems.map((item) => (
                      <div key={item.id} className="calc-cost-stack__row calc-cost-stack__row--indent">
                        <span className="calc-cost-stack__row-label">{item.label || '—'}</span>
                        <span className="calc-cost-stack__row-value">{currency(item.amount)}</span>
                      </div>
                    ))}

                    <div className="calc-cost-stack__divider" />

                    <div className="calc-cost-stack__row">
                      <span className="calc-cost-stack__row-label calc-cost-stack__row-label--bold">
                        Holding Costs
                        <span style={{ marginLeft: 4, fontWeight: 400, fontSize: 12, color: 'var(--clr-grey)' }}>({inp.holdingMonths} mo × {currency(r.monthlyHoldingCost)}/mo)</span>
                      </span>
                      <span className="calc-cost-stack__row-value calc-cost-stack__row-value--bold">{currency(r.holdingCostTotal)}</span>
                    </div>
                    {effectiveHoldingItems.map((item) => (
                      <div key={item.id} className="calc-cost-stack__row calc-cost-stack__row--indent">
                        <span className="calc-cost-stack__row-label">{item.label || '—'}</span>
                        <span className="calc-cost-stack__row-value">{currency(item.amount)}/mo</span>
                      </div>
                    ))}

                    <div className="calc-cost-stack__divider" />

                    <div className="calc-cost-stack__row">
                      <span className="calc-cost-stack__row-label calc-cost-stack__row-label--bold">Rehab Costs</span>
                      <span className="calc-cost-stack__row-value calc-cost-stack__row-value--bold">{currency(r.rehabCostTotal)}</span>
                    </div>

                    <div className="calc-cost-stack__divider" />

                    <div className="calc-cost-stack__row">
                      <span className="calc-cost-stack__row-label calc-cost-stack__row-label--bold">Selling Costs</span>
                      <span className="calc-cost-stack__row-value calc-cost-stack__row-value--bold">{currency(r.sellingCostTotal)}</span>
                    </div>
                    {sellingItems.map((item) => (
                      <div key={item.id} className="calc-cost-stack__row calc-cost-stack__row--indent">
                        <span className="calc-cost-stack__row-label">{item.label || '—'}</span>
                        <span className="calc-cost-stack__row-value">{currency(item.amount)}</span>
                      </div>
                    ))}

                    <div className="calc-cost-stack__divider--heavy">
                      <div className="calc-cost-stack__row">
                        <span className="calc-cost-stack__row-label calc-cost-stack__row-label--bold">Total Costs</span>
                        <span className="calc-cost-stack__row-value calc-cost-stack__row-value--bold" style={{ fontSize: 14 }}>{currency(r.totalCosts)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Right: profit waterfall */}
                  <div>
                    <p className="calc-cost-stack__heading">Deal Summary</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {[
                        { label: 'Sale Price',     v: r.salePrice,   color: 'var(--clr-charcoal)' },
                        { label: '− Total Costs',  v: r.totalCosts,  color: 'var(--clr-rose)', neg: true },
                        { label: '= Total Profit', v: r.totalProfit, color: isProfit ? '#5AC471' : 'var(--clr-rose)', bold: true },
                      ].map(({ label, v, color, bold, neg }) => (
                        <div key={label} className="calc-cost-stack__row" style={{ padding: '4px 0' }}>
                          <span className={`calc-cost-stack__row-label${bold ? ' calc-cost-stack__row-label--bold' : ''}`}>{label}</span>
                          <span className="calc-cost-stack__row-value calc-cost-stack__row-value--bold" style={{ color }}>{neg ? `(${currency(v)})` : currency(v)}</span>
                        </div>
                      ))}
                    </div>

                    <div className="calc-cost-stack__divider" />

                    {/* Profit bar */}
                    <div style={{ marginTop: 4 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--clr-grey)', marginBottom: 4 }}>
                        <span>$0</span><span>{currency(Math.max(r.totalCosts, r.salePrice))}</span>
                      </div>
                      <div className="calc-profit-bar">
                        {r.totalCosts > 0 && (
                          <div className="calc-profit-bar__fill" style={{ width: `${Math.min((r.totalCosts / Math.max(r.totalCosts, r.salePrice)) * 100, 100)}%`, backgroundColor: 'var(--clr-rose)' }} />
                        )}
                        {r.salePrice > 0 && (
                          <div className="calc-profit-bar__fill" style={{ width: `${Math.min((r.salePrice / Math.max(r.totalCosts, r.salePrice)) * 100, 100)}%`, backgroundColor: isProfit ? '#5AC471' : 'var(--clr-rose)', opacity: 0.7 }} />
                        )}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 4 }}>
                        <span style={{ color: 'var(--clr-grey)' }}>Cost vs Sale</span>
                        <span style={{ fontWeight: 600, color: isProfit ? '#5AC471' : 'var(--clr-rose)' }}>
                          {isProfit ? '▲' : '▼'} {pct(Math.abs(r.profitMargin))} {isProfit ? 'margin' : 'loss'}
                        </span>
                      </div>
                    </div>

                    <div style={{ marginTop: 16 }} className="calc-grid-2">
                      {[
                        { label: 'Purchase', v: r.purchaseCostTotal, color: '#128292' },
                        { label: 'Holding',  v: r.holdingCostTotal,  color: '#3D9AA7' },
                        { label: 'Rehab',    v: r.rehabCostTotal,    color: '#F16722' },
                        { label: 'Selling',  v: r.sellingCostTotal,  color: '#CA1551' },
                      ].map(({ label, v, color }) => (
                        <div key={label} className="calc-type-card">
                          <div className="calc-type-card__label">
                            <span className="calc-type-card__swatch" style={{ backgroundColor: color }} />
                            <span className="calc-type-card__name">{label}</span>
                          </div>
                          <div className="calc-type-card__value">{currency(v)}</div>
                          <div className="calc-type-card__sub">
                            {r.totalCosts > 0 ? pct(v / r.totalCosts) : '—'} of costs
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* REHAB LOG TAB */}
          {tab === 'Rehab Log' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <SectionBanner title="Rehab Transactions" />
                <button
                  onClick={() => {
                    const id = nextRId()
                    setRehab((prev) => [...prev, { id, date: '', costType: 'Labor', description: '', amount: 0 }])
                  }}
                  className="calc-add-btn"
                >
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Row
                </button>
              </div>

              <div className="calc-card calc-card--no-pad">
                <table className="calc-rehab-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Date</th>
                      <th style={{ textAlign: 'left' }}>Cost Type</th>
                      <th style={{ textAlign: 'left' }}>Description</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rehab.length === 0 && (
                      <tr><td colSpan={5} style={{ padding: '32px 0', textAlign: 'center', color: 'var(--clr-grey)', fontSize: 13 }}>No rehab items yet — click Add Row to get started</td></tr>
                    )}
                    {rehab.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <input
                            type="date"
                            value={row.date}
                            onChange={(e) => setRehab((prev) => prev.map((t) => t.id === row.id ? { ...t, date: e.target.value } : t))}
                          />
                        </td>
                        <td>
                          <select
                            value={row.costType}
                            onChange={(e) => setRehab((prev) => prev.map((t) => t.id === row.id ? { ...t, costType: e.target.value } : t))}
                          >
                            {COST_TYPES.map((t) => <option key={t}>{t}</option>)}
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            value={row.description}
                            placeholder="Description"
                            onChange={(e) => setRehab((prev) => prev.map((t) => t.id === row.id ? { ...t, description: e.target.value } : t))}
                          />
                        </td>
                        <td>
                          <div style={{ position: 'relative' }}>
                            <span style={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', color: 'var(--clr-grey)', fontSize: 13, pointerEvents: 'none' }}>$</span>
                            <NumInput
                              value={row.amount}
                              onChange={(n) => setRehab((prev) => prev.map((t) => t.id === row.id ? { ...t, amount: n } : t))}
                              style={{ textAlign: 'right', paddingLeft: 20 }}
                            />
                          </div>
                        </td>
                        <td style={{ textAlign: 'right', padding: '6px 8px' }}>
                          <button onClick={() => setRehab((prev) => prev.filter((t) => t.id !== row.id))} className="calc-delete-btn">
                            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {rehab.length > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan={3} style={{ fontSize: 12, fontWeight: 700, color: 'var(--clr-charcoal)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Total Rehab Costs ({rehab.length} items)
                        </td>
                        <td style={{ textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--clr-charcoal)', fontVariantNumeric: 'tabular-nums' }}>{currency(r.rehabCostTotal)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {rehab.length > 0 && (
                <>
                  <SectionBanner title="By Cost Type" />
                  <div className="calc-grid-5">
                    {COST_TYPES.map((type) => {
                      const total = rehab.filter((t) => t.costType === type).reduce((s, t) => s + (parseFloat(t.amount) || 0), 0)
                      return (
                        <div key={type} className="calc-type-card">
                          <div className="calc-type-card__name">{type}</div>
                          <div className="calc-type-card__value">{total > 0 ? currency(total) : '—'}</div>
                          {total > 0 && r.rehabCostTotal > 0 && (
                            <div className="calc-type-card__sub">{pct(total / r.rehabCostTotal)} of rehab</div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
})

export default FixFlipAnalyser
